import { createSlice } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import _ from 'lodash';
import { SearchStatus, TravelPolicyPolicies, userLocalPreferencesKey } from '../../constants';

const initialState = {
  loaded: false,
  isLoggedIn: false,
  email: '',
  token: null,
  error: '',
  authError: null,
  isPopupSignInInProgress: false,
  availableCountries: [],
  availableStates: [],
  hotelRateCodes: undefined,
  rentalCarDiscountCodes: undefined,
  flightRateCodes: undefined,
  travelAgencySearchStatus: SearchStatus.INITIAL,
  travelAgency: undefined,
  travelAgencyId: undefined,
  travelAgencyError: undefined,
  sendCustomerQueryStatus: SearchStatus.INITIAL,
  sendCustomerQueryError: '',
  isPagePasswordVerified: false,
  pagePasswordError: false,
  archRoamAgentId: null,
  userPreferences: {
    dismissedNotifications: [],
  },
};

const getRentalCarDiscountCodeLabel = (carRateCode, doHideMemberIdInCarRates) => {
  if (!carRateCode) return '';
  const {
    // oxlint-disable-next-line camelcase
    label,
    vehicle_vendor,
    membershipType,
    membershipId,
  } = carRateCode;
  const vendorName = vehicle_vendor?.vendorName || '';
  if (label)
    return doHideMemberIdInCarRates ? label : `${label} (${membershipType} - ${membershipId})`;
  return doHideMemberIdInCarRates
    ? vendorName
    : `${vendorName} (${membershipType} - ${membershipId})`;
};

const formatRentalCarDiscountCodesResponse = (carRateCodes, doHideMemberIdInCarRates) =>
  carRateCodes && carRateCodes.length
    ? _.map(carRateCodes, (c) => ({
        membershipId: c.membershipId,
        membershipType: c.membershipType,
        vendorCode: c.vehicle_vendor?.vendorCode,
        vendorName: c.vehicle_vendor?.vendorName,
        label: getRentalCarDiscountCodeLabel(c, doHideMemberIdInCarRates),
        isSelectedByDefault: c.isSelected,
        rateGroup: c.rateGroup,
        id: c.id,
      }))
    : [];

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginRequest: (state) => state,
    loginSuccess: (state, action) => ({
      ...state,
      token: action.payload.token,
      email: action.payload.email,
      name: action.payload.name,
      isLoggedIn: true,
      loaded: true,
      authError: null,
      defaultCity: action.payload.user && action.payload.user.defaultCity,
      /// TODO: Remove travelAgencyId. we can get travel agency id from travelAgency obj
      travelAgencyId: action.payload.travelAgencyId
        ? action.payload.travelAgencyId
        : state.travelAgencyId,
      travelAgency:
        action.payload.travelAgencyId && action.payload.travelAgencyId !== state.travelAgencyId
          ? undefined
          : state.travelAgency,
    }),
    loginFailure: (state, action) => ({
      ...initialState,
      error: action.payload,
    }),
    setLoaded: (state) => ({
      ...state,
      loaded: true,
    }),
    logout: (state) => {
      localStorage.removeItem('user');
      return {
        ...initialState,
        isLoggedIn: false,
        loaded: true,
        travelAgencyError: state.travelAgencyError,
        authError: state.authError,
        availableCountries: state.availableCountries,
        availableStates: state.availableStates,
        travelAgency: state.travelAgency,
        travelAgencyId: state.travelAgencyId,
        hotelRateCodes: state.hotelRateCodes,
        flightRateCodes: state.flightRateCodes,
        rentalCarDiscountCodes: state.rentalCarDiscountCodes,
        isPagePasswordVerified: state.isPagePasswordVerified,
      };
    },
    clearError: (state) => ({
      ...state,
      error: '',
    }),
    setAuthError: (state, action) => {
      const { code = '', description = '' } = action.payload || {};
      // admin blocks arrive as unauthorized/"user is blocked"; Auth0 brute-force protection
      // reports too_many_attempts/"...blocked after multiple consecutive login attempts..."
      const blockedCodes = ['unauthorized', 'too_many_attempts'];
      return {
        ...state,
        authError: {
          code,
          description,
          isBlocked: blockedCodes.includes(code) && /blocked/i.test(description),
        },
      };
    },
    clearAuthError: (state) => ({
      ...state,
      authError: null,
    }),
    setIsPopupSignInInProgress: (state, action) => ({
      ...state,
      isPopupSignInInProgress: action.payload,
    }),
    verifyPagePasswordRequest: (state) => ({
      ...state,
      pagePasswordError: undefined,
      isPagePasswordVerified: false,
      pagePasswordRequestState: SearchStatus.SEARCHING,
    }),
    verifyPagePasswordSuccess: (state, action) => ({
      ...state,
      pagePasswordError: !action.payload.response ? 'Wrong password entered.' : undefined,
      isPagePasswordVerified: action.payload.response,
      pagePasswordRequestState: SearchStatus.DONE,
    }),
    verifyPagePasswordFailure: (state, action) => ({
      ...state,
      pagePasswordError: action.payload,
      isPagePasswordVerified: false,
      pagePasswordRequestState: SearchStatus.DONE,
    }),
    availableCountriesRequest: (state) => ({
      ...state,
    }),
    availableCountriesSuccess: (state, action) => ({
      ...state,
      availableCountries: action.payload,
    }),
    availableCountriesFailure: (state, action) => ({
      ...state,
      error: action.payload,
    }),
    availableStatesRequest: (state) => ({
      ...state,
    }),
    availableStatesSuccess: (state, action) => ({
      ...state,
      availableStates: action.payload,
    }),
    availableStatesFailure: (state, action) => ({
      ...state,
      error: action.payload,
    }),
    getTravelAgencySettingsSuccess: (state, { payload: { data } }) => {
      const agencyTheme = data && data.siteTheme ? data.siteTheme : {};
      const siteTheme = { ...agencyTheme };
      if (agencyTheme.bgColorVar) {
        siteTheme['--background'] = agencyTheme.bgColorVar;
        delete siteTheme.bgColorVar;
      }
      if (agencyTheme.datePickersBgHex) {
        siteTheme['--datePickersBgHex'] = agencyTheme.datePickersBgHex;
        delete siteTheme.datePickersBgHex;
      }
      if (agencyTheme.borderColorVar) {
        siteTheme['--border'] = agencyTheme.borderColorVar;
        delete siteTheme.borderColorVar;
      }
      if (agencyTheme.textColorVar) {
        siteTheme['--content'] = agencyTheme.textColorVar;
        delete siteTheme.textColorVar;
      }
      if (agencyTheme.bgButtonColorVar) {
        siteTheme['--buttonBackground'] = agencyTheme.bgButtonColorVar;
        delete siteTheme.bgButtonColorVar;
      }
      if (agencyTheme.textButtonColorVar) {
        siteTheme['--textButtonBackground'] = agencyTheme.textButtonColorVar;
        delete siteTheme.textButtonColorVar;
      }
      if (agencyTheme.fontFamilyTagline) {
        siteTheme['--fontFamilyTagline'] = agencyTheme.fontFamilyTagline;
        delete siteTheme.fontFamilyTagline;
      }
      if (agencyTheme.fontFamilyBody) {
        siteTheme['--fontFamilyBody'] = agencyTheme.fontFamilyBody;
        delete siteTheme.fontFamilyBody;
      }
      if (agencyTheme.fontFamilyHeading) {
        siteTheme['--fontFamilyHeading'] = agencyTheme.fontFamilyHeading;
        delete siteTheme.fontFamilyHeading;
      }
      if (agencyTheme.fontFamilySubHeading) {
        siteTheme['--fontFamilySubHeading'] = agencyTheme.fontFamilySubHeading;
        delete siteTheme.fontFamilySubHeading;
      }
      if (agencyTheme.fontFamilySmallerHeading) {
        siteTheme['--fontFamilySmallerHeading'] = agencyTheme.fontFamilySmallerHeading;
        delete siteTheme.fontFamilySmallerHeading;
      }
      if (agencyTheme.fontSizeHeading) {
        siteTheme['--fontSizeHeading'] = agencyTheme.fontSizeHeading;
        delete siteTheme.fontSizeHeading;
      }
      if (agencyTheme.fontSizeSubHeading) {
        siteTheme['--fontSizeSubHeading'] = agencyTheme.fontSizeSubHeading;
        delete siteTheme.fontSizeSubHeading;
      }
      if (agencyTheme.fontSizeSmallerHeading) {
        siteTheme['--fontSizeSmallerHeading'] = agencyTheme.fontSizeSmallerHeading;
        delete siteTheme.fontSizeSmallerHeading;
      }
      if (agencyTheme.searchButtonColorHex) {
        siteTheme['--searchButtonColorHex'] = agencyTheme.searchButtonColorHex;
        delete siteTheme.searchButtonColorHex;
      }
      if (agencyTheme.homeFooterBgColorHex) {
        siteTheme['--homeFooterBgColorHex'] = agencyTheme.homeFooterBgColorHex;
        delete siteTheme.homeFooterBgColorHex;
      }
      if (agencyTheme.homeFooterTextColorHex) {
        siteTheme['--homeFooterTextColorHex'] = agencyTheme.homeFooterTextColorHex;
        delete siteTheme.homeFooterTextColorHex;
      }
      if (agencyTheme.footerBgColorHex) {
        siteTheme['--footerBgColorHex'] = agencyTheme.footerBgColorHex;
        delete siteTheme.footerBgColorHex;
      }
      if (agencyTheme.footerTextColorHex) {
        siteTheme['--footerTextColorHex'] = agencyTheme.footerTextColorHex;
        delete siteTheme.footerTextColorHex;
      }
      if (agencyTheme.pageBgColorHex) {
        siteTheme['--pageBgColorHex'] = agencyTheme.pageBgColorHex;
        delete siteTheme.pageBgColorHex;
      }
      if (agencyTheme.filtersBgOpacityWhenEmpty) {
        siteTheme['--filtersBgOpacityWhenEmpty'] = agencyTheme.filtersBgOpacityWhenEmpty;
        delete siteTheme.filtersBgOpacityWhenEmpty;
      }
      if (agencyTheme.filtersBgOpacityWhenNotEmpty) {
        siteTheme['--filtersBgOpacityWhenNotEmpty'] = agencyTheme.filtersBgOpacityWhenNotEmpty;
        delete siteTheme.filtersBgOpacityWhenNotEmpty;
      }
      if (agencyTheme.homeNavbarBgColorHex) {
        siteTheme['--homeNavbarBgColorHex'] = agencyTheme.homeNavbarBgColorHex;
        delete siteTheme.homeNavbarBgColorHex;
      }
      if (agencyTheme.headerBgColor) {
        siteTheme['--headerBgColor'] = agencyTheme.headerBgColor;
        delete siteTheme.headerBgColor;
      }
      if (agencyTheme.homeHeaderBgColor) {
        siteTheme['--homeHeaderBgColor'] = agencyTheme.homeHeaderBgColor;
        delete siteTheme.homeHeaderBgColor;
      }
      if (agencyTheme.headerTextColorHex) {
        siteTheme['--headerTextColorHex'] = agencyTheme.headerTextColorHex;
        delete siteTheme.headerTextColorHex;
      }
      if (agencyTheme.homeHeaderTextColorHex) {
        siteTheme['--homeHeaderTextColorHex'] = agencyTheme.homeHeaderTextColorHex;
        delete siteTheme.homeHeaderTextColorHex;
      }
      if (agencyTheme.logoAttributionColorHex) {
        siteTheme['--logoAttributionColorHex'] = agencyTheme.logoAttributionColorHex;
        delete siteTheme.logoAttributionColorHex;
      }
      if (agencyTheme.searchbarTabsTextColorHex) {
        siteTheme['--searchbarTabsTextColorHex'] = agencyTheme.searchbarTabsTextColorHex;
        delete siteTheme.searchbarTabsTextColorHex;
      }
      if (agencyTheme.topBarBgColorHex) {
        siteTheme['--topBarBgColorHex'] = agencyTheme.topBarBgColorHex;
        delete siteTheme.topBarBgColorHex;
      }
      if (agencyTheme.preferenceLabelColor) {
        siteTheme['--preferenceLabelColor'] = agencyTheme.preferenceLabelColor;
        delete siteTheme.preferenceLabelColor;
      }
      if (agencyTheme.searchFormBgColor) {
        siteTheme['--searchFormBgColor'] = agencyTheme.searchFormBgColor;
        siteTheme.doHaveSearchFormBgColor = true;
        delete siteTheme.searchFormBgColor;
      }
      if (agencyTheme.searchProgressbarBgColorHex) {
        siteTheme['--searchProgressbarBgColor'] = agencyTheme.searchProgressbarBgColorHex;
        delete siteTheme.searchProgressbarBgColorHex;
      }
      if (agencyTheme.searchProgressbarTextColorHex) {
        siteTheme['--searchProgressbarTextColor'] = agencyTheme.searchProgressbarTextColorHex;
        delete siteTheme.searchProgressbarTextColorHex;
      }
      if (agencyTheme.searchBtnTextColorHex) {
        siteTheme['--searchBtnTextColorHex'] = agencyTheme.searchBtnTextColorHex;
        delete siteTheme.searchBtnTextColorHex;
      }
      if (agencyTheme.sliderColorHex) {
        siteTheme['--sliderColorHex'] = agencyTheme.sliderColorHex;
        delete siteTheme.sliderColorHex;
      }
      if (agencyTheme.filterBgColor) {
        siteTheme['--filterBgColor'] = agencyTheme.filterBgColor;
        delete siteTheme.filterBgColor;
      }
      if (agencyTheme.searchInputBgColorHex) {
        siteTheme['--searchInputBgColorHex'] = agencyTheme.searchInputBgColorHex;
        delete siteTheme.searchInputBgColorHex;
      }
      if (agencyTheme.searchbarTabsClass) {
        siteTheme.searchbarTabsClass = agencyTheme.searchbarTabsClass;
      }
      if (agencyTheme.homeFilterBgColor) {
        siteTheme['--homeFilterBgColor'] = agencyTheme.homeFilterBgColor;
        delete siteTheme.homeFilterBgColor;
      }
      if (agencyTheme.homeSearchbarTabsTextColorHex) {
        siteTheme['--homeSearchbarTabsTextColorHex'] = agencyTheme.homeSearchbarTabsTextColorHex;
        delete siteTheme.homeSearchbarTabsTextColorHex;
      }
      if (agencyTheme.pageHeadingAlignClass) {
        siteTheme.pageHeadingAlignClass = agencyTheme.pageHeadingAlignClass;
      }

      if (agencyTheme.hotelAmenitiesTextColorHex) {
        siteTheme['--hotelAmenitiesTextColorHex'] = agencyTheme.hotelAmenitiesTextColorHex;
        delete siteTheme.hotelAmenitiesTextColorHex;
      }

      let isTravelAgencyBySite = false;
      if (data) {
        if (state.travelAgency && data.id === state.travelAgency.id) {
          isTravelAgencyBySite = state.travelAgency.isTravelAgencyBySite;
        } else {
          isTravelAgencyBySite = data.isTravelAgencyBySite;
        }
      }

      let { settings } = data || {};
      try {
        if (
          data &&
          data.settings &&
          data.settings.headerLinks &&
          typeof data.settings.headerLinks === 'string'
        ) {
          settings = {
            ...data.settings,
            headerLinks: JSON.parse(data.settings.headerLinks),
          };
        }
      } catch (e) {
        console.error('Error parsing header links');
      }

      const { usefulLinks } = data || {};
      const { hotelRateCodes } = data || {};
      const { flightRateCodes } = data || {};
      const { carRateCodes } = data || {};

      const { filterSettings } = data || {};
      const rentalCarDiscountCodes = formatRentalCarDiscountCodesResponse(
        carRateCodes,
        filterSettings?.doHideMemberIdInCarRates || false,
      );

      return {
        ...state,
        usefulLinks,
        hotelRateCodes,
        flightRateCodes,
        rentalCarDiscountCodes,
        travelAgency: data && {
          ...data,
          isTravelAgencyBySite,
          siteTheme,
          settings,
        },
        travelAgencyId: data && data.isTravelAgencyBySite ? undefined : (data || {}).id,
        travelAgencySearchStatus: SearchStatus.DONE,
      };
    },
    getTravelAgencySettingsFailed: (state, { payload }) => ({
      ...state,
      travelAgencyError: payload,
      travelAgencySearchStatus: SearchStatus.DONE,
    }),
    getTravelAgencySettingsRequest: (state) => ({
      ...state,
      travelAgencySearchStatus: SearchStatus.SEARCHING,
    }),
    getQuickLinksRequest: (state) => ({
      ...state,
    }),
    getQuickLinksSuccess: (state, { payload: { data } }) => {
      const usefulLinks = data;
      return {
        ...state,
        usefulLinks,
      };
    },
    getHotelRateCodesRequest: (state) => ({
      ...state,
    }),
    getHotelRateCodesSuccess: (state, { payload }) => ({
      ...state,
      hotelRateCodes: payload,
    }),
    getFlightRateCodesRequest: (state) => ({
      ...state,
    }),
    getFlightRateCodesSuccess: (state, { payload }) => ({
      ...state,
      flightRateCodes: payload,
    }),
    getRentalCarDiscountCodesRequest: (state) => ({
      ...state,
    }),
    getRentalCarDiscountCodesSuccess: (state, { payload }) => {
      const rentalCarDiscountCodes = formatRentalCarDiscountCodesResponse(payload);
      return {
        ...state,
        rentalCarDiscountCodes,
      };
    },
    resetSendCustomerQueryStatus: (state) => ({
      ...state,
      sendCustomerQueryStatus: SearchStatus.INITIAL,
    }),
    sendCustomerQueryRequest: (state) => ({
      ...state,
      sendCustomerQueryError: null,
      sendCustomerQueryStatus: SearchStatus.SEARCHING,
    }),
    sendCustomerQuerySuccess: (state, { error }) => ({
      ...state,
      sendCustomerQueryError: error,
      sendCustomerQueryStatus: SearchStatus.DONE,
    }),
    sendVerificationEmailRequest: (state) => ({
      ...state,
      sendVerificationEmailError: null,
      sendVerificationEmailStatus: SearchStatus.SEARCHING,
    }),
    sendVerificationEmailSuccess: (state, { payload: { error } }) => ({
      ...state,
      sendVerificationEmailError: error,
      sendVerificationEmailStatus: SearchStatus.DONE,
    }),
    setUserPref: (state, action) => ({
      ...state,
      userPreferences: {
        ...(state.userPreferences || {}),
        ...action.payload,
      },
    }),
    setArchRoamAgentId: (state, action) => ({
      ...state,
      archRoamAgentId: action.payload || null,
    }),
  },
  extraReducers: {
    [HYDRATE]: (state, action) => action.payload.user,
  },
});

export const checkIsLoggedIn = (state) => state.user.isLoggedIn;
export const checkDoShowAgentCommission = (state) => {
  if (state.user.isLoggedIn && (state?.[userLocalPreferencesKey] ?? {})?.doHideAgentCommission) {
    return false;
  }
  const isConfirmationPage =
    window && window.location && (window.location.pathname || '').indexOf('confirmation') > -1;
  const doShowCommissionOnTripConfirmationPage =
    state.user.travelAgency &&
    state.user.travelAgency.settings &&
    state.user.travelAgency.settings.doShowCommissionOnTripConfirmationPage;

  return (
    !state.brochure.isViewingBrochure &&
    !state.compare.isViewingCompare &&
    state.user.isLoggedIn &&
    (state.user.travelAgency || {}).doShowAgentCommission &&
    (!isConfirmationPage ||
      _.isEmpty(state.trip.tripConfirmation || {}) ||
      doShowCommissionOnTripConfirmationPage)
  );
};

export const checkErrors = (state) => state.user.error;

export const checkAuthError = (state) => state.user.authError;

export const checkIsPopupSignInInProgress = (state) => state.user.isPopupSignInInProgress;

export const checkIsLoadedIn = (state) => ({
  isLoggedIn: state.user.isLoggedIn,
  loaded: state.user.loaded,
});

export const getSiteHeaderVariant = (state) =>
  (state.user.travelAgency &&
    state.user.travelAgency.isTravelAgencyBySite &&
    state.user.travelAgency.siteHeaderVariant) ||
  0;

export const getTravelAgency = (state) => ({
  ...(state.user.travelAgency ? state.user.travelAgency : {}),
});

export const getUserPermissions = (state) => {
  if (state.user.travelAgency) {
    if (!state.user.isLoggedIn) {
      if (
        state.user.travelAgency.permissions &&
        state.user.travelAgency.permissions.anonymous &&
        state.user.travelAgency.permissions.anonymous.length > 0
      ) {
        return state.user.travelAgency.permissions.anonymous;
      }
      return [];
    }

    if (
      state.user.isLoggedIn &&
      state.user.travelAgency.permissions &&
      state.user.travelAgency.permissions.authenticated &&
      state.user.travelAgency.permissions.authenticated.length > 0
    ) {
      return state.user.travelAgency.permissions.authenticated;
    }
    return [];
  }
  return null;
};

export const getTravelPolicyLeadDays = (travelType) => (state) => {
  const policies =
    (state && state.user && state.user.travelAgency && state.user.travelAgency.travelPolicies) ||
    [];

  // Filter policies
  const bookingLeadDays = _.filter(policies, {
    policy: TravelPolicyPolicies.BookingLeadDays,
  });

  // Early return if no matching policies
  if (!bookingLeadDays || bookingLeadDays.length === 0) {
    return 0;
  }

  // Find the first matching setting for the travelType
  const matchedSetting = _.find(
    bookingLeadDays,
    (setting) =>
      setting &&
      setting.searchModes &&
      Array.isArray(setting.searchModes) &&
      setting.searchModes.indexOf(travelType) !== -1,
  );

  // Return matched value if found
  if (matchedSetting && matchedSetting.value) {
    return parseInt(matchedSetting.value, 10) || 0;
  }

  // Fallback to first policy value if no type-specific match
  const firstPolicy = _.find(bookingLeadDays, (b) => !b.searchModes || !b.searchModes.length);
  if (firstPolicy && firstPolicy.value) {
    return parseInt(firstPolicy.value, 10) || 0;
  }

  return 0;
};

export const checkSitePermission = (permission) => (state) => {
  const permissions = getUserPermissions(state);
  return permissions && permissions.indexOf(permission) > -1;
};

export const checkFoldMultipleRateCodes = (isLoggedIn, travelAgency) => {
  const settings = travelAgency?.settings || {};
  const { doFoldMultipleRateCodes, doFoldMultipleRateCodesAnonymousUser } = settings;
  if (isLoggedIn) return !!doFoldMultipleRateCodes;
  return !!doFoldMultipleRateCodesAnonymousUser;
};

export const checkDoShowTripConfirmationPricing = (state) =>
  !(state.user.travelAgency && state.user.travelAgency.defaultDoShowPricingToAnonymous)
    ? state.user.isLoggedIn
    : true;

export const checkDoEnableSelectFlightsBySegment = (state) => {
  const isSearchPage =
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.pathname || '').toLowerCase().includes('/search');
  if (isSearchPage) return false;
  return (
    state.user.travelAgency &&
    state.user.travelAgency.settings &&
    state.user.travelAgency.settings.doEnableSelectFlightsBySegment
  );
};
export const checkDoEnableDirectFlightSearch = (state) =>
  true ||
  (state.user.travelAgency &&
    state.user.travelAgency.settings &&
    state.user.travelAgency.settings.doEnableDirectFlightSearch);

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  setLoaded,
  clearError,
  setAuthError,
  clearAuthError,
  setIsPopupSignInInProgress,
  availableCountriesRequest,
  availableCountriesSuccess,
  availableCountriesFailure,
  availableStatesRequest,
  availableStatesSuccess,
  availableStatesFailure,
  getTravelAgencySettingsSuccess,
  getTravelAgencySettingsRequest,
  getTravelAgencySettingsFailed,
  getQuickLinksRequest,
  getQuickLinksSuccess,
  setUserPref,
  getHotelRateCodesRequest,
  getHotelRateCodesSuccess,
  getFlightRateCodesRequest,
  getFlightRateCodesSuccess,
  getRentalCarDiscountCodesRequest,
  getRentalCarDiscountCodesSuccess,
  resetSendCustomerQueryStatus,
  sendCustomerQueryRequest,
  sendCustomerQuerySuccess,
  sendVerificationEmailRequest,
  sendVerificationEmailSuccess,
  verifyPagePasswordRequest,
  verifyPagePasswordSuccess,
  verifyPagePasswordFailure,
  setArchRoamAgentId,
} = userSlice.actions;

export const selectArchRoamAgentId = (state) => state.user.archRoamAgentId || undefined;

export default userSlice.reducer;
